import React, { useEffect, useRef, useState } from "react";
import {
  Alert, Button, Card, DatePicker, Form, Input, InputNumber, message, Modal,
  List, Select, Space, Statistic, Table, Tag, Typography, Upload,
} from "antd";
import {
  DeleteOutlined, DownloadOutlined, EditOutlined, PaperClipOutlined,
  EyeOutlined, PlusOutlined, ReloadOutlined, StopOutlined, UploadOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { hasPermission } from "../auth/permissions";
import { listAllActiveBankAccounts } from "../services/bankAccountApi";
import { listUsers } from "../services/userApi";
import {
  createInternalTransfer, createTransaction, getAccountBalance, listTransactions,
  updateTransaction, voidTransaction,
} from "../services/transactionApi";
import {
  deleteReceipt, downloadReceipt, listReceipts, uploadReceipt,
} from "../services/receiptApi";

const EMPTY_PAGE = { content: [], page: 0, size: 20, totalElements: 0 };
const MONEY = new Intl.NumberFormat("vi-VN", {
  style: "currency", currency: "VND", maximumFractionDigits: 0,
});
const DATE_TIME = new Intl.DateTimeFormat("vi-VN", {
  dateStyle: "short", timeStyle: "short",
});

function payloadFrom(values, accountId, editing) {
  return {
    bankAccountId: accountId,
    type: values.type,
    amount: Number(values.amount),
    transactionAt: values.transactionAt.toISOString(),
    content: values.content?.trim() || null,
    reference: values.reference?.trim() || null,
    version: editing?.version ?? null,
  };
}

function accountLabel(account) {
  if (!account) return "";
  return `${account.internalCode} · ${account.bankName} · ${account.accountNumber || account.accountNumberMasked || "—"}`;
}

export default function Transactions({ currentUser }) {
  const canWrite = hasPermission(currentUser, "transactions:edit");
  const canDelete = hasPermission(currentUser, "transactions:delete");
  const canManageReceipts = canWrite && currentUser?.role === "OPERATOR";
  const canDeleteReceipts = canDelete && currentUser?.role === "OPERATOR";
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [pageData, setPageData] = useState(EMPTY_PAGE);
  const [balance, setBalance] = useState(null);
  const [filters, setFilters] = useState({ status: "", type: "", actorId: "", from: "", through: "", query: "" });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [editing, setEditing] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [voiding, setVoiding] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [receiptTransaction, setReceiptTransaction] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const pending = useRef(false);
  const sequence = useRef(0);
  const [form] = Form.useForm();
  const [voidForm] = Form.useForm();
  const [transferForm] = Form.useForm();

  async function load(nextAccountId, page = 0, size = pageData.size || 20, nextFilters = filters) {
    if (!nextAccountId) {
      setPageData(EMPTY_PAGE);
      setBalance(null);
      return;
    }
    const requestSequence = ++sequence.current;
    setLoading(true);
    setLoadError("");
    try {
      const [transactions, currentBalance] = await Promise.all([
        listTransactions({ bankAccountId: nextAccountId, page, size, ...nextFilters }),
        getAccountBalance(nextAccountId),
      ]);
      if (requestSequence === sequence.current) {
        setPageData(transactions);
        setBalance(currentBalance.balance);
      }
    } catch (error) {
      if (requestSequence === sequence.current) {
        setLoadError(error.message || "Không thể tải sổ giao dịch");
      }
    } finally {
      if (requestSequence === sequence.current) setLoading(false);
    }
  }

  useEffect(() => {
    let mounted = true;
    listAllActiveBankAccounts()
      .then((items) => {
        if (!mounted) return;
        setAccounts(items);
        const firstId = items[0]?.id || "";
        setAccountId(firstId);
        if (firstId) load(firstId, 0, 20, { status: "", type: "", actorId: "", from: "", through: "", query: "" });
      })
      .catch((error) => mounted && setLoadError(error.message || "Không thể tải tài khoản"));
    listUsers({ page: 0, size: 100 })
      .then((page) => mounted && setUsers(page.content || []))
      .catch(() => {});
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    if (receiptPreview?.url) URL.revokeObjectURL(receiptPreview.url);
  }, [receiptPreview]);

  function selectAccount(value) {
    setAccountId(value);
    load(value, 0, pageData.size, filters);
  }

  function openCreate() {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ type: "OUT", transactionAt: moment() });
    setEditorOpen(true);
  }

  function openEdit(record) {
    setEditing(record);
    form.setFieldsValue({ ...record, transactionAt: moment(record.transactionAt) });
    setEditorOpen(true);
  }

  async function save(values) {
    if (pending.current) return;
    pending.current = true;
    setSaving(true);
    try {
      const payload = payloadFrom(values, accountId, editing);
      if (editing) await updateTransaction(editing.id, payload);
      else await createTransaction(payload);
      message.success(editing ? "Đã cập nhật giao dịch" : "Đã thêm giao dịch");
      setEditorOpen(false);
      setEditing(null);
      form.resetFields();
      await load(accountId, pageData.page, pageData.size);
    } catch (error) {
      message.error(error.message || "Không thể lưu giao dịch");
    } finally {
      pending.current = false;
      setSaving(false);
    }
  }

  async function confirmVoid(values) {
    if (!voiding || pending.current) return;
    pending.current = true;
    setSaving(true);
    try {
      await voidTransaction(voiding.id, values.reason.trim());
      message.success("Đã hủy giao dịch và giữ lại lịch sử");
      setVoiding(null);
      voidForm.resetFields();
      await load(accountId, pageData.page, pageData.size);
    } catch (error) {
      message.error(error.message || "Không thể hủy giao dịch");
    } finally {
      pending.current = false;
      setSaving(false);
    }
  }

  async function saveTransfer(values) {
    if (pending.current) return;
    pending.current = true;
    setSaving(true);
    try {
      await createInternalTransfer({
        sourceAccountId: accountId,
        destinationAccountId: values.destinationAccountId,
        amount: Number(values.amount),
        transactionAt: values.transactionAt.toISOString(),
        content: values.content?.trim() || null,
        reference: values.reference?.trim() || null,
      });
      message.success("Đã chuyển tiền và tạo đủ hai giao dịch liên kết");
      setTransferOpen(false);
      transferForm.resetFields();
      await load(accountId, pageData.page, pageData.size);
    } catch (error) {
      message.error(error.message || "Không thể chuyển tiền nội bộ");
    } finally {
      pending.current = false;
      setSaving(false);
    }
  }

  async function openReceipts(record) {
    setReceiptTransaction(record);
    setReceiptsLoading(true);
    try {
      setReceipts(await listReceipts(record.id));
    } catch (error) {
      message.error(error.message || "Không thể tải biên lai");
    } finally {
      setReceiptsLoading(false);
    }
  }

  async function uploadReceiptFile(file) {
    if (!receiptTransaction || pending.current) return false;
    if (file.size > 10 * 1024 * 1024) {
      message.error("Biên lai tối đa 10MB");
      return false;
    }
    pending.current = true;
    setReceiptsLoading(true);
    try {
      await uploadReceipt(receiptTransaction.id, file);
      setReceipts(await listReceipts(receiptTransaction.id));
      message.success("Đã tải biên lai lên");
    } catch (error) {
      message.error(error.message || "Không thể tải biên lai lên");
    } finally {
      pending.current = false;
      setReceiptsLoading(false);
    }
    return false;
  }

  async function downloadReceiptFile(attachment) {
    try {
      const blob = await downloadReceipt(receiptTransaction.id, attachment.id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = attachment.originalFilename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      message.error(error.message || "Không thể tải biên lai");
    }
  }

  async function viewReceiptFile(attachment) {
    setPreviewLoading(true);
    try {
      const blob = await downloadReceipt(receiptTransaction.id, attachment.id);
      setReceiptPreview({
        url: URL.createObjectURL(blob),
        contentType: attachment.contentType || blob.type,
        filename: attachment.originalFilename,
      });
    } catch (error) {
      message.error(error.message || "Không thể xem biên lai");
    } finally {
      setPreviewLoading(false);
    }
  }

  async function removeReceipt(attachment) {
    setReceiptsLoading(true);
    try {
      await deleteReceipt(receiptTransaction.id, attachment.id);
      setReceipts(await listReceipts(receiptTransaction.id));
      message.success("Đã xóa biên lai khỏi danh sách");
    } catch (error) {
      message.error(error.message || "Không thể xóa biên lai");
    } finally {
      setReceiptsLoading(false);
    }
  }

  const selectedAccount = accounts.find((item) => item.id === accountId);
  const columns = [
    { title: "Mã giao dịch", dataIndex: "transactionCode", width: 190 },
    {
      title: "Thời gian", dataIndex: "transactionAt", width: 145,
      render: (value) => DATE_TIME.format(new Date(value)),
    },
    {
      title: "Loại", dataIndex: "type", width: 90,
      render: (value) => (
        <Tag color={value === "IN" ? "green" : "volcano"}>
          {value === "IN" ? "Tiền vào" : "Tiền ra"}
        </Tag>
      ),
    },
    {
      title: "Số tiền", dataIndex: "amount", align: "right", width: 150,
      render: (value, row) => (
        <span className={row.type === "IN" ? "money-in" : "money-out"}>
          {row.type === "IN" ? "+" : "-"}{MONEY.format(value)}
        </span>
      ),
    },
    { title: "Nội dung", dataIndex: "content", render: (value) => value || "—" },
    { title: "Tham chiếu", dataIndex: "reference", width: 140, render: (value) => value || "—" },
    {
      title: "Người tạo",
      dataIndex: "createdByName",
      width: 150,
      render: (value, row) => value || row.createdBy || "—",
    },
    {
      title: "Cập nhật bởi",
      dataIndex: "updatedByName",
      width: 150,
      render: (value, row) => value || row.updatedBy || "—",
    },
    {
      title: "Trạng thái", dataIndex: "status", width: 110,
      render: (value) => (
        <Tag color={value === "ACTIVE" ? "blue" : "default"}>
          {value === "ACTIVE" ? "Hiệu lực" : "Đã hủy"}
        </Tag>
      ),
    },
    {
      title: "Thao tác",
      key: "actions",
      width: canWrite ? 220 : 100,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={canManageReceipts ? <PaperClipOutlined /> : <EyeOutlined />}
            onClick={() => openReceipts(record)}
          >
            {canManageReceipts ? "Biên lai" : "Xem biên lai"}
          </Button>
          {canWrite && record.status === "ACTIVE" && (
            <>
              <Button type="link" icon={<EditOutlined />} onClick={() => openEdit(record)}>Sửa</Button>
              {canDelete && <Button type="link" danger icon={<StopOutlined />} onClick={() => setVoiding(record)}>Hủy</Button>}
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="management-page">
      <div className="page-heading">
        <div>
          <Typography.Title level={2}>Giao dịch tiền vào / ra</Typography.Title>
          <Typography.Text type="secondary">
            Sổ giao dịch theo từng tài khoản; giao dịch hủy vẫn được lưu để đối chiếu.
          </Typography.Text>
        </div>
      </div>

      <div className="transaction-summary">
        <Card>
          <Typography.Text type="secondary">Tài khoản đang xem</Typography.Text>
          <Select
            showSearch
            optionFilterProp="children"
            value={accountId || undefined}
            placeholder="Chọn tài khoản"
            onChange={selectAccount}
            style={{ width: "100%", marginTop: 8 }}
          >
            {accounts.map((account) => (
              <Select.Option key={account.id} value={account.id}>
                {accountLabel(account)}
              </Select.Option>
            ))}
          </Select>
        </Card>
        <Card>
          <Statistic
            title="Số dư hiện tại"
            value={balance ?? 0}
            formatter={(value) => MONEY.format(Number(value))}
            valueStyle={{ color: balance < 0 ? "#cf1322" : "#096dd9" }}
          />
          {selectedAccount && (
            <Typography.Text type="secondary">
              Số dư đầu: {MONEY.format(selectedAccount.openingBalance)}
            </Typography.Text>
          )}
        </Card>
      </div>

      <div className="filter-bar">
        <Input.Search
          allowClear
          placeholder="Mã, nội dung, tham chiếu..."
          value={filters.query}
          onChange={(event) => setFilters((current) => ({ ...current, query: event.target.value }))}
          onSearch={() => load(accountId, 0, pageData.size)}
          style={{ maxWidth: 320 }}
        />
        <Select
          value={filters.type}
          onChange={(type) => {
            const next = { ...filters, type };
            setFilters(next);
            load(accountId, 0, pageData.size, next);
          }}
          style={{ width: 140 }}
        >
          <Select.Option value="">Tất cả loại</Select.Option>
          <Select.Option value="IN">Tiền vào</Select.Option>
          <Select.Option value="OUT">Tiền ra</Select.Option>
        </Select>
        <Select
          value={filters.status}
          onChange={(status) => {
            const next = { ...filters, status };
            setFilters(next);
            load(accountId, 0, pageData.size, next);
          }}
          style={{ width: 150 }}
        >
          <Select.Option value="">Tất cả trạng thái</Select.Option>
          <Select.Option value="ACTIVE">Hiệu lực</Select.Option>
          <Select.Option value="VOIDED">Đã hủy</Select.Option>
        </Select>
        <Select
          showSearch
          optionFilterProp="children"
          value={filters.actorId}
          onChange={(actorId) => {
            const next = { ...filters, actorId };
            setFilters(next);
            load(accountId, 0, pageData.size, next);
          }}
          style={{ width: 210 }}
        >
          <Select.Option value="">Tất cả người thao tác</Select.Option>
          {users.map((user) => (
            <Select.Option key={user.id} value={user.id}>
              {user.displayName || user.username} · @{user.username}
            </Select.Option>
          ))}
        </Select>
        <DatePicker.RangePicker
          showTime
          format="DD/MM/YYYY HH:mm"
          style={{ width: 330 }}
          onChange={(range) => {
            const next = {
              ...filters,
              from: range?.[0]?.toISOString() || "",
              through: range?.[1]?.toISOString() || "",
            };
            setFilters(next);
            load(accountId, 0, pageData.size, next);
          }}
        />
        <Button icon={<ReloadOutlined />} onClick={() => load(accountId, pageData.page, pageData.size)}>
          Tải lại
        </Button>
        {canWrite && accountId && (
          <div className="transaction-primary-actions">
            <Button
              disabled={accounts.length < 2}
              onClick={() => {
                transferForm.setFieldsValue({ transactionAt: moment() });
                setTransferOpen(true);
              }}
            >
              Chuyển nội bộ
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Thêm giao dịch
            </Button>
          </div>
        )}
      </div>

      {loadError && <Alert className="page-alert" type="error" showIcon message={loadError} />}
      <Table
        rowKey="id"
        columns={columns}
        dataSource={pageData.content}
        loading={loading}
        scroll={{ x: 1450 }}
        locale={{ emptyText: accountId ? "Chưa có giao dịch" : "Chưa có tài khoản được phân công" }}
        pagination={{
          current: pageData.page + 1,
          pageSize: pageData.size,
          total: pageData.totalElements,
          showSizeChanger: true,
          showTotal: (total) => `${total} giao dịch`,
          onChange: (page, size) => load(accountId, page - 1, size),
        }}
      />

      <Modal
        visible={editorOpen}
        title={editing ? `Sửa ${editing.transactionCode}` : "Thêm giao dịch"}
        okText="Lưu giao dịch"
        cancelText="Đóng"
        confirmLoading={saving}
        onOk={() => form.submit()}
        onCancel={() => !saving && setEditorOpen(false)}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          message="Tài khoản giao dịch"
          description={selectedAccount
            ? `${accountLabel(selectedAccount)} · Số dư hiện tại ${MONEY.format(balance ?? 0)}`
            : "Chưa chọn tài khoản"}
          style={{ marginBottom: 16 }}
        />
        <Form form={form} layout="vertical" onFinish={save}>
          <Form.Item name="type" label="Loại giao dịch" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="IN">Tiền vào</Select.Option>
              <Select.Option value="OUT">Tiền ra</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="amount" label="Số tiền (VND)" rules={[{ required: true, message: "Nhập số tiền" }]}>
            <InputNumber
              min={1}
              precision={0}
              style={{ width: "100%" }}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
              parser={(value) => value.replace(/\./g, "")}
            />
          </Form.Item>
          <Form.Item name="transactionAt" label="Thời gian giao dịch" rules={[{ required: true }]}>
            <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="content" label="Nội dung">
            <Input.TextArea maxLength={1000} rows={3} />
          </Form.Item>
          <Form.Item name="reference" label="Mã tham chiếu / số chứng từ">
            <Input maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        visible={Boolean(voiding)}
        title={`Hủy giao dịch ${voiding?.transactionCode || ""}?`}
        okText="Xác nhận hủy"
        okButtonProps={{ danger: true }}
        cancelText="Đóng"
        confirmLoading={saving}
        onOk={() => voidForm.submit()}
        onCancel={() => !saving && setVoiding(null)}
        destroyOnClose
      >
        <Alert type="warning" showIcon message="Giao dịch không bị xóa và sẽ còn trong lịch sử kiểm tra." />
        <Form form={voidForm} layout="vertical" onFinish={confirmVoid} style={{ marginTop: 16 }}>
          <Form.Item
            name="reason"
            label="Lý do hủy"
            rules={[
              { required: true, whitespace: true, message: "Vui lòng nhập lý do hủy" },
              { max: 500, message: "Tối đa 500 ký tự" },
            ]}
          >
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        visible={transferOpen}
        title="Chuyển tiền nội bộ"
        okText="Tạo lệnh chuyển"
        cancelText="Đóng"
        confirmLoading={saving}
        onOk={() => transferForm.submit()}
        onCancel={() => !saving && setTransferOpen(false)}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          message={`Tài khoản chuyển: ${selectedAccount?.internalCode || ""}`}
        />
        <Form
          form={transferForm}
          layout="vertical"
          onFinish={saveTransfer}
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="destinationAccountId"
            label="Tài khoản nhận"
            rules={[{ required: true, message: "Chọn tài khoản nhận" }]}
          >
            <Select placeholder="Chọn tài khoản nhận">
              {accounts.filter((account) => account.id !== accountId).map((account) => (
                <Select.Option key={account.id} value={account.id}>
                  {accountLabel(account)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="amount" label="Số tiền (VND)" rules={[{ required: true }]}>
            <InputNumber min={1} precision={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="transactionAt" label="Thời gian chuyển" rules={[{ required: true }]}>
            <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="content" label="Nội dung">
            <Input.TextArea rows={2} maxLength={1000} />
          </Form.Item>
          <Form.Item name="reference" label="Mã tham chiếu">
            <Input maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        visible={Boolean(receiptTransaction)}
        title={`Biên lai · ${receiptTransaction?.transactionCode || ""}`}
        footer={[
          <Button key="close" onClick={() => setReceiptTransaction(null)}>Đóng</Button>,
        ]}
        onCancel={() => setReceiptTransaction(null)}
        width={640}
      >
        {canManageReceipts && (
          <Upload
            accept=".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf"
            showUploadList={false}
            beforeUpload={uploadReceiptFile}
            disabled={receiptsLoading || receipts.length >= 10}
          >
            <Button icon={<UploadOutlined />}>
              Tải biên lai lên ({receipts.length}/10)
            </Button>
          </Upload>
        )}
        <List
          style={{ marginTop: 16 }}
          loading={receiptsLoading}
          dataSource={receipts}
          locale={{ emptyText: "Chưa có biên lai" }}
          renderItem={(attachment) => (
            <List.Item
              actions={[
                <Button
                  key="view"
                  type="link"
                  loading={previewLoading}
                  icon={<EyeOutlined />}
                  onClick={() => viewReceiptFile(attachment)}
                >
                  Xem
                </Button>,
                <Button
                  key="download"
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={() => downloadReceiptFile(attachment)}
                >
                  Tải xuống
                </Button>,
                canDeleteReceipts ? (
                  <Button
                    key="delete"
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeReceipt(attachment)}
                  >
                    Xóa
                  </Button>
                ) : null,
              ].filter(Boolean)}
            >
              <List.Item.Meta
                avatar={<PaperClipOutlined />}
                title={attachment.originalFilename}
                description={`${attachment.contentType} · ${(attachment.fileSize / 1024).toFixed(1)} KB`}
              />
            </List.Item>
          )}
        />
      </Modal>

      <Modal
        visible={Boolean(receiptPreview)}
        title={`Xem biên lai · ${receiptPreview?.filename || ""}`}
        width={900}
        footer={[
          <Button key="close-preview" onClick={() => setReceiptPreview(null)}>Đóng</Button>,
        ]}
        onCancel={() => setReceiptPreview(null)}
        destroyOnClose
      >
        {receiptPreview?.contentType === "application/pdf" ? (
          <iframe
            title={receiptPreview.filename}
            src={receiptPreview.url}
            style={{ width: "100%", height: "70vh", border: 0 }}
          />
        ) : receiptPreview ? (
          <div style={{ textAlign: "center", overflow: "auto" }}>
            <img
              src={receiptPreview.url}
              alt={receiptPreview.filename}
              style={{ maxWidth: "100%", maxHeight: "70vh" }}
            />
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
